import type { DeviceListScope } from 'src/service/api/deviceList';
import type { DeviceNavTab } from 'src/service/api/deviceNav';
import type { DeviceListColumnId } from 'src/component/v2/DeviceList/DeviceList';

export const DEVICE_NAV_TABS: readonly DeviceNavTab[] = ['panels', 'groups', 'connections'];

export interface DeviceNavSelection {
  tab: DeviceNavTab;
  nodeId?: string;
  childId?: string;
}

const NODE_SCOPE_KEY = {
  panels: 'panelId',
  groups: 'groupId',
  connections: 'commLoopId',
} as const;

const BASE_COLUMNS: readonly DeviceListColumnId[] = ['controller', 'circuit', 'alarms', 'workOrders'];

/* With nothing selected, the tab's own grouping column leads. */
const ALL_COLUMNS: Record<DeviceNavTab, readonly DeviceListColumnId[]> = {
  panels: ['area', 'panel', ...BASE_COLUMNS],
  groups: ['group', ...BASE_COLUMNS],
  connections: ['connection', ...BASE_COLUMNS],
};

export function toDeviceListScope ({ tab, nodeId, childId }: DeviceNavSelection): DeviceListScope | undefined {
  const scope: DeviceListScope = {};
  if (nodeId) {
    scope[NODE_SCOPE_KEY[tab]] = nodeId;
  } else if (tab === 'groups') {
    scope.groupBy = 'group';
  } else if (tab === 'connections') {
    scope.groupBy = 'connection';
  }
  if (childId) {
    scope.controllerId = childId;
  }
  return Object.keys(scope).length ? scope : undefined;
}

export function columnsForSelection (
  { tab, nodeId, childId }: DeviceNavSelection,
  areaSelected = false
): readonly DeviceListColumnId[] {
  if (nodeId || childId) {
    return BASE_COLUMNS;
  }
  if (areaSelected) {
    return ALL_COLUMNS[tab].filter((column) => column !== 'area');
  }
  return ALL_COLUMNS[tab];
}

const isTab = (value: string | null): value is DeviceNavTab =>
  DEVICE_NAV_TABS.includes(value as DeviceNavTab);

/* The selection as URL params (?nav=groups&node=<id>&device=<id>). Other params are left alone. */
export function selectionFromUrl (queryString: string): DeviceNavSelection {
  const params = new URLSearchParams(queryString);
  const tab = params.get('nav');
  if (!isTab(tab)) {
    return { tab: 'panels' };
  }
  const selection: DeviceNavSelection = { tab };
  const nodeId = params.get('node');
  const childId = params.get('device');
  if (nodeId) selection.nodeId = nodeId;
  if (nodeId && childId) selection.childId = childId;
  return selection;
}

export function selectionToUrl (selection: DeviceNavSelection, queryString = ''): string {
  const params = new URLSearchParams(queryString);
  ['nav', 'node', 'device'].forEach((key) => params.delete(key));
  if (selection.tab !== 'panels' || selection.nodeId) {
    params.set('nav', selection.tab);
  }
  if (selection.nodeId) params.set('node', selection.nodeId);
  if (selection.nodeId && selection.childId) params.set('device', selection.childId);
  const query = params.toString();
  return query ? `?${query}` : '';
}
